// src/data/destinations.ts
//
// Déménagement international : une page par pays — LOT 17 du plan SEO.
//
// Une page pays n'est pas un texte libre : c'est un ensemble de données
// réglementaires, chacune avec sa source officielle et sa date de relevé,
// autour desquelles le copywriter écrit. Ce module est l'accès typé à cette
// donnée (src/data/destinations.json, régénéré au build depuis la table
// `destinations`), et son validateur — l'unique implémentation des règles,
// appelée par les tests et par le contrôle de build via le bundle SSR.
//
// Deux verrous, tous deux nécessaires pour qu'une page soit servie :
//   · `published` — en base, coché dans le back-office ;
//   · `verifiePar: 'human'` + `dateVerification` — dans le DÉPÔT seulement,
//     jamais écrasés par la synchronisation. Un enregistrement CMS ne lève pas
//     un verrou de publication : c'est la règle posée pour la fiche
//     stationnement des communes, et elle vaut ici pour les mêmes raisons.
//
// Ce qui n'existe PAS ici, et c'est une décision : aucun délai de livraison,
// aucune fréquence de groupage, aucun prix par destination. Les devis sont
// uniques et au cas par cas ; le validateur refuse tout chiffre de ce type
// dans les textes d'une page publiée, plutôt que d'espérer qu'on s'en
// abstienne.
import donnees from './destinations.json';
import supports from './international-supports.json';
import { COMMUNES } from './communes';
import { PAYS_ETRANGERS } from './registre';

/** Racine du silo : le pilier, existant, conservé. */
export const RACINE_INTERNATIONAL = '/demenagement/demenagement-international';

/**
 * Un fait réglementaire, tel qu'il est publié : la procédure en clair, QUI
 * l'affirme (l'URL officielle, et l'ancre rédigée du lien) et QUAND on l'a
 * lue. Un bloc sans source n'est pas un bloc « en attente » : il n'existe
 * pas, et la page dit alors qu'elle ne sait pas.
 */
export interface BlocReglementaire {
  texte: string;
  sourceUrl: string;
  /** Ancre du lien vers la source, rédigée — jamais composée par formule. */
  libelleSource: string;
  dateVerification: string;
}

export interface VilleDestination {
  nom: string;
  /** Ancre stable de la section H3 (`id`), liable depuis le pilier : slug de la ville. */
  id: string;
  /** Depuis le dépôt, comme pour les communes ; null tant que non mesurée. */
  distanceKm: number | null;
  /** Contrainte d'accès réelle pour un camion — ZTL, AOT, zone environnementale — sourcée, ou null. */
  contraintesAcces: BlocReglementaire | null;
}

export interface Destination {
  /** `belgique-<pays>` : segment d'URL sous RACINE_INTERNATIONAL. */
  slug: string;
  paysNom: string;
  paysCodeIso: string;
  drapeau: string;
  section: 'international';
  published: boolean;
  publishedAt: string | null;
  navOrder: number;
  verifiePar: 'human' | null;
  dateVerification: string | null;
  distanceKmDepotCapitale: number | null;
  tempsTrajetIndicatif: string | null;
  /** Luxembourg, Pays-Bas, Allemagne : voisinage transfrontalier, pas export. */
  euregio: boolean;
  ue: boolean;
  douaneRequise: boolean;
  /** L'angle éditorial imposé (§ 4), rappelé à qui rédige. */
  angle: string;
  titreMeta: string | null;
  descriptionMeta: string | null;
  h1: string | null;
  chapeau: string | null;
  trajet: string | null;
  ceQueGrammeFait: string | null;
  /** Résumé seulement : le détail vit sur la page douane-formalites. */
  formalitesDepartBelgique: string | null;
  douane: BlocReglementaire | null;
  formalitesArrivee: BlocReglementaire | null;
  vehicule: BlocReglementaire | null;
  animaux: BlocReglementaire | null;
  inverseFormalitesBelgique: BlocReglementaire | null;
  /** Slugs de communes Gramme, liés comme villes d'arrivée. */
  inverseVillesArrivee: string[];
  /** Deux slugs de destinations, déclarés — jamais calculés. */
  voisins: string[];
  villes: VilleDestination[];
  faq: Array<{ question: string; reponse: string }>;
  aValider: string[];
}

/** Page support du silo (§ 5) : structure éditoriale, dans le dépôt. */
export interface SectionSupport {
  titre: string;
  /** Paragraphes, séparés par une ligne vide. Absent quand la section n'est faite que de sous-sections. */
  texte?: string;
  /**
   * Source officielle d'une section qui affirme une règle (vague C : la page
   * « S'installer en Belgique » dit ce qu'un arrivant doit faire à la commune).
   * Rendue comme un bloc réglementaire de page pays : « Information vérifiée
   * en … sur … ». Absente sur une section qui ne parle que de Gramme.
   */
  source?: SourceOfficielle;
  /** H3 sous le H2 — un facteur du devis, une procédure, chacune avec sa source si elle en affirme une. */
  sousSections?: Array<{ titre: string; texte: string; source?: SourceOfficielle }>;
  /** Liens internes proposés sous la section — pages communes d'arrivée, zones d'intervention. */
  liens?: Array<{ libelle: string; to: string }>;
}

/** Une source officielle sans texte : ce qu'un bloc réglementaire porte en plus de sa procédure. */
export type SourceOfficielle = Pick<BlocReglementaire, 'sourceUrl' | 'libelleSource' | 'dateVerification'>;

/**
 * Procédure séquentielle d'une page support — vague D, la sortie de Belgique.
 * Réservée à ce qui est réellement ordonné : on déclare son départ AVANT de
 * s'inscrire au consulat, et le consulat demande le modèle 8. Le JSON-LD
 * `HowTo` reproduit exactement ces étapes, ni plus ni moins, et le contrôle de
 * build compte les deux. Une question de FAQ n'est pas une étape.
 */
export interface ProcedureSupport {
  titre: string;
  intro?: string;
  etapes: Array<{ titre: string; texte: string; source?: SourceOfficielle }>;
}

export interface PageSupportInternational {
  slug: string;
  vague: string;
  publiable: boolean;
  verifiePar: 'human' | null;
  dateVerification: string | null;
  titreMeta: string;
  descriptionMeta: string;
  h1: string;
  chapeau: string;
  /** Les H2 attendus tant que la page n'est pas rédigée ; rendus avec un marqueur. */
  plan: string[];
  /** Le contenu rédigé (17-C). Présent : `plan` n'est plus rendu. */
  sections?: SectionSupport[];
  /** La seule procédure pas à pas du silo : quitter la Belgique (§ 5, page douane). */
  howTo?: ProcedureSupport;
  /** Encadré « À retenir », 40 à 60 mots — le passage que les moteurs conversationnels extraient. */
  aRetenir?: string;
  faq?: Array<{ question: string; reponse: string }>;
  aValider: string[];
}

const D = donnees as unknown as { destinations: Destination[] };
const S = supports as unknown as { pages: PageSupportInternational[] };

export const DESTINATIONS: readonly Destination[] = D.destinations;
export const SUPPORTS_INTERNATIONAL: readonly PageSupportInternational[] = S.pages;

/** Les blocs réglementaires d'une destination, nommés comme sur la page. */
export const BLOCS_REGLEMENTAIRES = [
  ['douane', 'Douane'],
  ['formalitesArrivee', "Formalités d'arrivée"],
  ['vehicule', 'Véhicule'],
  ['animaux', 'Animaux'],
  ['inverseFormalitesBelgique', 'Formalités en Belgique (sens inverse)'],
] as const;
export type CleBloc = (typeof BLOCS_REGLEMENTAIRES)[number][0];

/** URL d'une page pays : /demenagement/demenagement-international/belgique-<pays>. */
export function cheminDestination(slug: string): string {
  return `${RACINE_INTERNATIONAL}/${slug}`;
}

/** URL d'une page support, sous le même préfixe. */
export function cheminSupport(slug: string): string {
  return `${RACINE_INTERNATIONAL}/${slug}`;
}

export function destinationParSlug(slug: string | undefined): Destination | undefined {
  return slug ? DESTINATIONS.find((d) => d.slug === slug) : undefined;
}

export function supportParSlug(slug: string | undefined): PageSupportInternational | undefined {
  return slug ? SUPPORTS_INTERNATIONAL.find((p) => p.slug === slug) : undefined;
}

/** Toutes les destinations, dans l'ordre du menu (`nav_order`), puis par nom. */
export function destinationsOrdonnees(): Destination[] {
  return DESTINATIONS.slice().sort((a, b) => a.navOrder - b.navOrder || a.paysNom.localeCompare(b.paysNom, 'fr'));
}

/** La page est-elle relue et datée ? Le verrou du dépôt. */
export function destinationVerifiee(d: Destination): boolean {
  return d.verifiePar === 'human' && Boolean(d.dateVerification);
}

/**
 * Les destinations réellement servies : publiées en base ET relues dans le
 * dépôt. C'est cette liste — et aucune autre — que lisent l'accueil, le
 * pilier, le pied de page, le sitemap et le pré-rendu.
 */
export function destinationsPubliees(): Destination[] {
  return destinationsOrdonnees().filter((d) => d.published && destinationVerifiee(d));
}

/**
 * Les pages supports réellement servies : publiables ET relues.
 *
 * Même verrou que `destinationsPubliees()` pour les pays, et pour la même
 * raison : une page support déclarée mais non relue ne doit être liée nulle
 * part. Le pied de page contextuel s'en sert depuis le lot 2 du chantier
 * maillage — jusque-là il ne proposait que le hub et les huit pays, laissant
 * les trois supports hors de la circulation interne de leur propre section.
 */
export function supportsPublies(): PageSupportInternational[] {
  return SUPPORTS_INTERNATIONAL.filter((s) => s.publiable && supportVerifie(s));
}

/**
 * Intitulé court d'une page support, pour un pied de page ou une liste.
 *
 * Les `h1` du dépôt sont des titres de page — « Douane et formalités :
 * déménager de Belgique vers l'étranger » — trop longs pour une colonne de
 * pied de page. Ces libellés-ci sont écrits pour être lus en liste, et
 * restent des ANCRES DISTINCTES les unes des autres.
 */
export function libelleSupport(slug: string): string {
  return LIBELLES_SUPPORTS[slug] ?? slug;
}

const LIBELLES_SUPPORTS: Record<string, string> = {
  'prix-demenagement-international': "Prix d'un déménagement international",
  'douane-formalites': 'Douane et formalités',
  's-installer-en-belgique': "S'installer en Belgique",
};

/** Les pays du catalogue, dans l'ordre du menu — publiés ou non. */
export function paysDesDestinations(): string[] {
  return destinationsOrdonnees().map((d) => d.paysNom);
}

/** « 2026-09-06 » → « septembre 2026 ». */
export function moisAnnee(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-BE', { month: 'long', year: 'numeric' });
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────────────────────

export interface ProblemeDestination {
  ou: string;
  gravite: 'erreur' | 'avertissement';
  message: string;
}

/**
 * Ce que la décision « aucun chiffre » interdit dans un texte : un délai en
 * jours, une fréquence de départs, un montant. La visite technique et le
 * devis tranchent au cas par cas ; la page le dit, elle ne le contourne pas.
 * Les mots sont testés sans accent pour que « départ » et « depart » tombent
 * pareil.
 */
const CHIFFRE_INTERDIT = [
  // Un délai de livraison : une fourchette de jours (« 1 à 5 jours »), ou un
  // nombre de jours dans la même phrase qu'une livraison ou un trajet. Un
  // délai ADMINISTRATIF sourcé (« s'inscrire dans les 8 jours ») reste admis :
  // c'est un fait réglementaire, pas un engagement de Gramme.
  /\b\d+\s*(?:a|à|-|–)\s*\d+\s*jours?\b/i,
  /\b(?:livraison|livr[ée]s?|acheminement|transit|trajet|chargement)\b[^.!?]{0,80}?\b\d+\s*jours?\b/i,
  /\b\d+\s*jours?\b[^.!?]{0,80}?\b(?:livraison|livr[ée]s?|acheminement|transit)\b/i,
  // Une fréquence de groupage.
  /\b(?:\d+|un|une|deux|trois|plusieurs)\s*(?:fois|d[ée]parts?|camions?|tourn[ée]es?|groupages?)\b[^.!?]{0,30}?\bpar\s*(?:semaine|mois|quinzaine)\b/i,
  /\b(?:d[ée]parts?|groupages?|camions?|tourn[ée]es?)\b[^.!?]{0,40}?\b(?:chaque|toutes? les)\s*(?:semaines?|quinzaines?|deux semaines|mois)\b/i,
  // Un prix.
  /\d\s*(?:€|euros?)(?![a-z])/i,
  /(?:^|[^a-z])(?:a|à) partir de\s*\d/i,
];

/** Le premier chiffre interdit trouvé, ou null. */
export function chiffreInterdit(texte: string): string | null {
  for (const re of CHIFFRE_INTERDIT) {
    const m = re.exec(texte);
    if (m) return m[0];
  }
  return null;
}

const URL_HTTPS = /^https:\/\/[^/\s]+(\/|$)/;

function textesDe(d: Destination): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const pousser = (ou: string, t: string | null | undefined) => { if (t) out.push([ou, t]); };
  pousser('titreMeta', d.titreMeta);
  pousser('descriptionMeta', d.descriptionMeta);
  pousser('h1', d.h1);
  pousser('chapeau', d.chapeau);
  pousser('trajet', d.trajet);
  pousser('ceQueGrammeFait', d.ceQueGrammeFait);
  pousser('formalitesDepartBelgique', d.formalitesDepartBelgique);
  for (const [cle] of BLOCS_REGLEMENTAIRES) pousser(cle, d[cle]?.texte);
  for (const v of d.villes) pousser(`villes.${v.id}`, v.contraintesAcces?.texte);
  for (const [i, f] of d.faq.entries()) { pousser(`faq[${i}].question`, f.question); pousser(`faq[${i}].reponse`, f.reponse); }
  return out;
}

export function validerDestinations(
  destinations: readonly Destination[] = DESTINATIONS,
  aujourdhui: Date = new Date(),
): ProblemeDestination[] {
  const p: ProblemeDestination[] = [];
  const limite = new Date(aujourdhui);
  limite.setUTCFullYear(limite.getUTCFullYear() - 1);
  const perimee = (iso: string | null | undefined) => Boolean(iso) && new Date(iso as string) < limite;
  const slugs = new Set(destinations.map((d) => d.slug));
  const communes = new Map(COMMUNES.map((c) => [c.id, c]));

  for (const s of new Set(destinations.map((d) => d.slug).filter((x, i, t) => t.indexOf(x) !== i))) {
    p.push({ ou: s, gravite: 'erreur', message: 'slug présent plusieurs fois' });
  }

  // Les pays du catalogue sont EXACTEMENT ceux du registre (couverture.pays,
  // hors Belgique). Le registre reste la source du fait « pays desservis »
  // — C4 du plan correctif — et cette table ne peut pas en diverger : un
  // pays ajouté d'un côté sans l'autre est une erreur de build.
  const pays = destinations.map((d) => d.paysNom).sort();
  const registre = PAYS_ETRANGERS.slice().sort();
  if (pays.join('|') !== registre.join('|')) {
    p.push({
      ou: 'destinations',
      gravite: 'erreur',
      message: `les pays des destinations (${pays.join(', ')}) ne sont pas ceux du registre couverture.pays (${registre.join(', ')})`,
    });
  }

  const questions = new Map<string, string>();
  const ordres = new Map<number, string>();

  for (const d of destinations) {
    const ou = d.slug;
    if (!/^belgique-[a-z0-9-]+$/.test(d.slug)) p.push({ ou, gravite: 'erreur', message: 'slug attendu sous la forme belgique-<pays>' });
    if (!/^[A-Z]{2}$/.test(d.paysCodeIso)) p.push({ ou, gravite: 'erreur', message: `code ISO « ${d.paysCodeIso} » invalide` });
    if (d.section !== 'international') p.push({ ou, gravite: 'erreur', message: `section « ${d.section} » — attendu international` });
    if (d.douaneRequise === d.ue) p.push({ ou, gravite: 'erreur', message: 'douaneRequise doit valoir le contraire de ue' });
    if (d.verifiePar !== 'human' && d.verifiePar !== null) p.push({ ou, gravite: 'erreur', message: `verifiePar vaut « ${d.verifiePar} » — attendu "human" ou null` });
    if (ordres.has(d.navOrder)) p.push({ ou, gravite: 'erreur', message: `navOrder ${d.navOrder} déjà pris par ${ordres.get(d.navOrder)}` });
    ordres.set(d.navOrder, d.slug);

    // Voisins : deux, existants, jamais soi-même. Pas de réciprocité exigée :
    // ce sont des voisinages géographiques OU logistiques (§ 6), et deux
    // voisins par page ne forment pas un graphe symétrique sur huit pays.
    if (d.voisins.includes(d.slug)) p.push({ ou, gravite: 'erreur', message: 'se cite elle-même comme voisine' });
    for (const v of d.voisins) {
      if (!slugs.has(v)) p.push({ ou, gravite: 'erreur', message: `voisine inexistante : « ${v} »` });
    }
    if (d.voisins.length !== 2) p.push({ ou, gravite: 'erreur', message: `${d.voisins.length} voisin(s) — il en faut exactement deux` });

    // Communes d'arrivée : des communes Gramme publiées, Liège au minimum.
    for (const c of d.inverseVillesArrivee) {
      const commune = communes.get(c);
      if (!commune) p.push({ ou, gravite: 'erreur', message: `commune d'arrivée inexistante : « ${c} »` });
      else if (commune.statut !== 'published') p.push({ ou, gravite: 'erreur', message: `commune d'arrivée « ${c} » en brouillon — le lien mènerait à une 404` });
    }
    if (!d.inverseVillesArrivee.includes('liege')) p.push({ ou, gravite: 'erreur', message: 'Liège manque aux villes d\'arrivée' });

    // Villes : ancres uniques, en forme de slug.
    const ancres = d.villes.map((v) => v.id);
    if (new Set(ancres).size !== ancres.length) p.push({ ou, gravite: 'erreur', message: 'deux villes portent la même ancre' });
    for (const v of d.villes) {
      if (!/^[a-z0-9-]+$/.test(v.id)) p.push({ ou, gravite: 'erreur', message: `ancre de ville « ${v.id} » : slug attendu` });
      if (v.distanceKm !== null && v.distanceKm < 0) p.push({ ou, gravite: 'erreur', message: `distance négative pour ${v.nom}` });
    }

    // Chaque bloc renseigné porte sa source ET sa date. Toujours — pas
    // seulement publié : un bloc collecté sans source n'est pas un bloc.
    const blocs: Array<[string, BlocReglementaire | null]> = [
      ...BLOCS_REGLEMENTAIRES.map(([cle]) => [cle, d[cle]] as [string, BlocReglementaire | null]),
      ...d.villes.map((v) => [`villes.${v.id}.contraintesAcces`, v.contraintesAcces] as [string, BlocReglementaire | null]),
    ];
    for (const [nom, bloc] of blocs) {
      if (!bloc) continue;
      if (!bloc.texte?.trim()) p.push({ ou, gravite: 'erreur', message: `${nom} : texte vide` });
      if (!bloc.sourceUrl?.trim()) p.push({ ou, gravite: 'erreur', message: `${nom} : sourceUrl manquante — un fait sans source ne se publie pas` });
      else if (!URL_HTTPS.test(bloc.sourceUrl)) p.push({ ou, gravite: 'erreur', message: `${nom} : sourceUrl « ${bloc.sourceUrl} » n'est pas une URL https` });
      if (!bloc.libelleSource?.trim()) p.push({ ou, gravite: 'erreur', message: `${nom} : libelleSource manquant — l'ancre du lien doit être rédigée` });
      if (!/^\d{4}-\d{2}-\d{2}$/.test(bloc.dateVerification ?? '')) p.push({ ou, gravite: 'erreur', message: `${nom} : dateVerification manquante ou hors format AAAA-MM-JJ` });
      else if (perimee(bloc.dateVerification)) p.push({ ou, gravite: 'avertissement', message: `${nom} : vérifié il y a plus de 12 mois (${bloc.dateVerification})` });
    }
    // Un pays hors UE sans bloc douane renseigné : la page ne peut rien dire
    // de la douane, et c'est précisément ce qu'elle doit dire. Bloquant dès
    // la publication.
    if (d.douaneRequise && !d.douane && d.published) p.push({ ou, gravite: 'erreur', message: 'douane requise mais bloc douane non renseigné' });
    if (!d.douaneRequise && d.douane) p.push({ ou, gravite: 'erreur', message: 'pays de l\'UE avec un bloc douane — la page doit dire « aucune », pas décrire une procédure' });

    // FAQ : questions non répétées d'un pays à l'autre.
    for (const f of d.faq) {
      if (!f.question?.trim() || !f.reponse?.trim()) p.push({ ou, gravite: 'erreur', message: 'faq : question ou réponse vide' });
      const cle = f.question.trim().toLowerCase();
      const deja = questions.get(cle);
      if (deja) p.push({ ou, gravite: 'erreur', message: `question de FAQ déjà posée sur ${deja} : « ${f.question} »` });
      else questions.set(cle, d.slug);
    }

    if (perimee(d.dateVerification)) p.push({ ou, gravite: 'avertissement', message: `page relue il y a plus de 12 mois (${d.dateVerification})` });
    if (d.published && !destinationVerifiee(d)) p.push({ ou, gravite: 'erreur', message: 'published=true en base mais page non relue dans le dépôt (verifiePar / dateVerification)' });
    if (!d.published && d.verifiePar === null && (d.h1 || d.chapeau)) {
      p.push({ ou, gravite: 'avertissement', message: 'contenu de travail non relu (verifiePar: null) — marqueurs « À VALIDER » rendus' });
    }

    // Ce qu'une page PUBLIÉE doit avoir, et ne doit pas avoir.
    if (d.published) {
      if (!d.titreMeta || d.titreMeta.length > 60) p.push({ ou, gravite: 'erreur', message: `titreMeta absent ou de plus de 60 caractères` });
      if (!d.descriptionMeta || d.descriptionMeta.length > 155) p.push({ ou, gravite: 'erreur', message: `descriptionMeta absente ou de plus de 155 caractères` });
      if (!d.h1) p.push({ ou, gravite: 'erreur', message: 'h1 absent' });
      if (!d.chapeau) p.push({ ou, gravite: 'erreur', message: 'chapeau absent' });
      if (!d.trajet) p.push({ ou, gravite: 'erreur', message: 'section « ce que le trajet implique » absente' });
      if (!d.formalitesDepartBelgique) p.push({ ou, gravite: 'erreur', message: 'résumé des formalités de départ absent' });
      if (!d.formalitesArrivee) p.push({ ou, gravite: 'erreur', message: 'bloc formalités d\'arrivée absent — le H3 du gabarit ne peut pas être vide' });
      if (!d.inverseFormalitesBelgique) p.push({ ou, gravite: 'erreur', message: 'bloc du sens inverse (pays → Belgique) absent — ce sens n\'est pas un appendice' });
      if (d.faq.length < 5 || d.faq.length > 7) p.push({ ou, gravite: 'erreur', message: `faq : ${d.faq.length} question(s) — il en faut entre 5 et 7` });
      if (d.villes.length < 1 || d.villes.length > 6) p.push({ ou, gravite: 'erreur', message: `${d.villes.length} ville(s) — entre 1 et 6` });
      if (!d.publishedAt) p.push({ ou, gravite: 'erreur', message: 'publishedAt manquant' });
      for (const [champ, texte] of textesDe(d)) {
        if (/\[PROVISOIRE\]/.test(texte)) p.push({ ou, gravite: 'erreur', message: `${champ} : marqueur [PROVISOIRE] sur une page publiée` });
        const chiffre = chiffreInterdit(texte);
        if (chiffre) p.push({ ou, gravite: 'erreur', message: `${champ} : « ${chiffre} » — aucun délai, fréquence ni prix chiffré ; le devis est unique et au cas par cas` });
      }
    }
  }

  return p;
}

/** Une page support est-elle relue et datée ? */
export function supportVerifie(s: PageSupportInternational): boolean {
  return s.verifiePar === 'human' && Boolean(s.dateVerification);
}

export function validerSupports(supportsAValider: readonly PageSupportInternational[] = SUPPORTS_INTERNATIONAL): ProblemeDestination[] {
  const p: ProblemeDestination[] = [];
  const slugs = supportsAValider.map((s) => s.slug);
  for (const s of new Set(slugs.filter((x, i) => slugs.indexOf(x) !== i))) p.push({ ou: s, gravite: 'erreur', message: 'slug présent plusieurs fois' });
  const destinations = new Set(DESTINATIONS.map((d) => d.slug));
  for (const s of supportsAValider) {
    if (destinations.has(s.slug)) p.push({ ou: s.slug, gravite: 'erreur', message: 'slug déjà pris par une destination — même préfixe d\'URL' });
    if (/^belgique-/.test(s.slug)) p.push({ ou: s.slug, gravite: 'erreur', message: 'un support ne commence pas par belgique- : ce préfixe est celui des pages pays' });
    if (s.publiable && !supportVerifie(s)) p.push({ ou: s.slug, gravite: 'erreur', message: 'publiable=true mais page non relue' });
    if (s.publiable) {
      // Une page publiée est une page rédigée : des sections, une FAQ, un
      // encadré ; et rien de ce que la décision « aucun chiffre » interdit.
      if (!s.sections || s.sections.length < 3) p.push({ ou: s.slug, gravite: 'erreur', message: 'publiable=true sans sections rédigées (trois au moins)' });
      if (!s.faq || s.faq.length < 3 || s.faq.length > 7) p.push({ ou: s.slug, gravite: 'erreur', message: `faq : ${s.faq?.length ?? 0} question(s) — il en faut entre 3 et 7` });
      if (!s.aRetenir) p.push({ ou: s.slug, gravite: 'erreur', message: 'encadré « à retenir » absent' });
      if (s.titreMeta.length > 60) p.push({ ou: s.slug, gravite: 'erreur', message: `titreMeta de ${s.titreMeta.length} caractères (max 60)` });
      if (s.descriptionMeta.length > 155) p.push({ ou: s.slug, gravite: 'erreur', message: `descriptionMeta de ${s.descriptionMeta.length} caractères (max 155)` });
      const textes: Array<[string, string]> = [['titreMeta', s.titreMeta], ['descriptionMeta', s.descriptionMeta], ['h1', s.h1], ['chapeau', s.chapeau]];
      if (s.aRetenir) textes.push(['aRetenir', s.aRetenir]);
      // Une source déclarée, où qu'elle soit (section, sous-section, étape),
      // obéit à la règle des blocs réglementaires : https, libellé, date.
      const verifierSource = (champ: string, src: SourceOfficielle | undefined) => {
        if (!src) return;
        if (!URL_HTTPS.test(src.sourceUrl ?? '')) p.push({ ou: s.slug, gravite: 'erreur', message: `${champ} : sourceUrl absente ou non https` });
        if (!src.libelleSource?.trim()) p.push({ ou: s.slug, gravite: 'erreur', message: `${champ} : libelleSource absent` });
        if (!/^\d{4}-\d{2}-\d{2}$/.test(src.dateVerification ?? '')) p.push({ ou: s.slug, gravite: 'erreur', message: `${champ} : dateVerification absente ou mal formée` });
      };

      // La procédure pas à pas : au moins trois étapes, chacune rédigée. Le
      // gabarit rend une ancre par étape et le JSON-LD les reprend une à une.
      if (s.howTo) {
        if (!s.howTo.titre?.trim()) p.push({ ou: s.slug, gravite: 'erreur', message: 'howTo : titre absent' });
        if (s.howTo.etapes.length < 3) p.push({ ou: s.slug, gravite: 'erreur', message: `howTo : ${s.howTo.etapes.length} étape(s) — une procédure en compte au moins trois` });
        textes.push(['howTo.titre', s.howTo.titre ?? '']);
        if (s.howTo.intro) textes.push(['howTo.intro', s.howTo.intro]);
        for (const [i, e] of s.howTo.etapes.entries()) {
          if (!e.titre?.trim() || !e.texte?.trim()) p.push({ ou: s.slug, gravite: 'erreur', message: `howTo.etapes[${i}] : titre ou texte vide` });
          textes.push([`howTo.etapes[${i}]`, `${e.titre} ${e.texte}`]);
          verifierSource(`howTo.etapes[${i}].source`, e.source);
        }
      }

      for (const [i, sec] of (s.sections ?? []).entries()) {
        textes.push([`sections[${i}].titre`, sec.titre]);
        if (sec.texte) textes.push([`sections[${i}].texte`, sec.texte]);
        for (const [j, ss] of (sec.sousSections ?? []).entries()) textes.push([`sections[${i}].sousSections[${j}]`, `${ss.titre} ${ss.texte}`]);
        verifierSource(`sections[${i}].source`, sec.source);
        for (const [j, ss] of (sec.sousSections ?? []).entries()) verifierSource(`sections[${i}].sousSections[${j}].source`, ss.source);
        for (const [k, l] of (sec.liens ?? []).entries()) {
          if (!l.libelle?.trim() || !l.to?.startsWith('/')) p.push({ ou: s.slug, gravite: 'erreur', message: `sections[${i}].liens[${k}] : libellé vide ou lien non interne` });
        }
      }
      for (const [i, f] of (s.faq ?? []).entries()) textes.push([`faq[${i}]`, `${f.question} ${f.reponse}`]);
      for (const [champ, t] of textes) {
        if (/\[PROVISOIRE\]/.test(t)) p.push({ ou: s.slug, gravite: 'erreur', message: `${champ} : marqueur [PROVISOIRE] sur une page publiée` });
        const chiffre = chiffreInterdit(t);
        if (chiffre) p.push({ ou: s.slug, gravite: 'erreur', message: `${champ} : « ${chiffre} » — aucun chiffre sur les pages supports` });
      }
    }
  }
  return p;
}
